import { TASK_STATUS_LABELS, type TaskSource } from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { aehnlicheFinden } from '../lib/aehnlichkeit'
import { trpc } from '../lib/trpc'
import { useAufgabenDieserWelt } from '../lib/welt'
import { Button, SourceBadge } from './ui/primitives'
import { hintergrundSchliesst } from './ui/hintergrund'
import { nebenBestandVerwerfen } from '../lib/mutationen'

/**
 * **Zwei Aufgaben, die dasselbe meinen, zu einer machen**
 * ([Z-11](../../../../docs/phasen/zusatz-11-zusammenfuehren.md),
 * [E-261](../../../../docs/ENTSCHEIDUNGEN.md#e-261)).
 *
 * Nils, 28.08.2026: *„Habe manchmal Aufgaben die sich irgendwie doppeln, aber jede für sich ein
 * paar Infos enthält. Das wäre schön wenn man die dann zusammenführen könnte."*
 *
 * ## Drei Schritte, und der zweite ist der wichtige
 *
 * 1. **Welche?** – Vorgeschlagen wird, was ähnlich heißt (`lib/aehnlichkeit.ts`, dasselbe Maß
 *    wie beim „Gibt es das schon?"-Knopf im Anlegen-Formular). Wer nichts Passendes darunter
 *    findet, sucht wie überall über Titel und Ticketnummer.
 * 2. **Was passiert?** – Die Vorschau vom Server: was mitkommt, was aufgefüllt wird, **was
 *    nicht übernommen wird**. Der Vorgang ist der einzige in Work, der eine Aufgabe löscht, und
 *    er ist nicht umkehrbar. Ein „Sind Sie sicher?" wäre hier nichts wert; die Frage, die jemand
 *    vor dem Klick tatsächlich hat, lautet *was verliere ich?* – und die Antwort lautet seit dem
 *    28.08.2026 **keinen Text**: Titel und Beschreibung der verschwindenden Aufgabe stehen
 *    danach im Verlauf und in einem Kommentar
 *    ([E-276](../../../../docs/ENTSCHEIDUNGEN.md#e-276)).
 * 3. **Welche bleibt?** – Vorgeschlagen wird die geöffnete, weil man von ihr aus hierherkommt.
 *    Tauschen ist ein Klick, und die Vorschau rechnet sofort neu: welche der beiden weiterlebt,
 *    hängt daran, welche schon in einem Pool liegt und welche im Quellsystem verlinkt ist – das
 *    kann die Maschine nicht raten.
 *
 * ## Warum kein „Verknüpfen" daneben steht
 *
 * Weil zwei verknüpfte Aufgaben zwei Zeilen in jeder Liste bleiben, zwei Pools belegen und zwei
 * Bearbeiter tragen können – also genau das Problem, dessentwegen man hier ist, nur mit einer
 * Fußnote versehen. Von Nils am 28.08.2026 so entschieden.
 */
export function AufgabeZusammenfuehren({
  aufgabeId,
  titel,
  partnerVorgewaehlt = null,
  schliessen,
  fertig,
}: {
  aufgabeId: string
  titel: string
  /**
   * **Die zweite Aufgabe steht schon fest** (16.09.2026) – aus „Alle Aufgaben", wo zwei Zeilen
   * angehakt sind. Das Fenster überspringt dann Schritt 1 und beginnt bei der Vorschau;
   * „Andere wählen" führt zur Suche zurück, „andersherum" tauscht wie sonst. Ohne Angabe
   * bleibt alles wie aus dem Aufgabendetail: erst suchen, dann wählen.
   */
  partnerVorgewaehlt?: string | null
  schliessen: () => void
  /** Läuft nach dem Verschmelzen – das Detail lädt neu, die Listen werden ungültig. */
  fertig: (behaltenId: string) => void
}) {
  const queryClient = useQueryClient()
  /**
   * **Nur die eigene Arbeitswelt** – dieselbe Verengung wie beim Zuordnen, und aus demselben
   * Grund: der Server lässt ein Verschmelzen über die Grenze ohnehin nicht zu (E-262). Eine
   * Auswahl anzubieten, die danach abgelehnt wird, wäre eine Falle mit Vorlauf.
   */
  const { aufgaben } = useAufgabenDieserWelt()

  const [suche, setSuche] = useState('')
  const [partnerId, setPartnerId] = useState<string | null>(partnerVorgewaehlt)
  /** `true` = die geöffnete Aufgabe bleibt. Der Normalfall, weil man von ihr aus hierherkommt. */
  const [dieseBleibt, setDieseBleibt] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)

  /**
   * **Was ähnlich heißt, steht oben – ohne dass jemand tippt.**
   *
   * Das ist der eigentliche Wert des Fensters: wer eine Dublette *sucht*, kennt meist nur eine
   * der beiden Formulierungen. Gerechnet wird im Client über den ohnehin geladenen Bestand
   * (Leitplanke 2), kostet also nichts und braucht keinen Spinner.
   *
   * **Nur Titel gegen Titel**, ohne die Beschreibung: der geladene Bestand führt sie nicht mit
   * (das täte er auch besser nicht – 3.100 Beschreibungen im Browser), und das ist hier die
   * schärfere der beiden Lesarten. Beim Anlegen ist es umgekehrt richtig; dort liegt der
   * Entwurfstext ohnehin im Formular.
   */
  const vorschlaege = useMemo(
    () =>
      aehnlicheFinden(
        { titel },
        aufgaben.filter((a) => a.id !== aufgabeId),
      ).map((t) => t.eintrag),
    [aufgaben, aufgabeId, titel],
  )

  const gesucht = suche.trim().toLowerCase()
  const treffer = useMemo(() => {
    if (!gesucht) return vorschlaege
    return aufgaben
      .filter((a) => a.id !== aufgabeId)
      .filter(
        (a) =>
          a.title.toLowerCase().includes(gesucht) ||
          (a.alleKennungen ?? '').toLowerCase().includes(gesucht),
      )
      .slice(0, 50)
  }, [aufgaben, aufgabeId, gesucht, vorschlaege])

  const behaltenId = dieseBleibt ? aufgabeId : (partnerId ?? aufgabeId)
  const aufloesenId = dieseBleibt ? partnerId : aufgabeId

  /**
   * Die Vorschau steht **auf dem Server**, obwohl der Bestand im Client liegt. Gezählt werden
   * Kommentare, Ereignisse, Unteraufgaben und Anhänge – nichts davon ist Teil des Bestands, und
   * eine geratene Zahl wäre in genau diesem Fenster am schädlichsten.
   */
  const { data: vorschau, isLoading: vorschauLaedt } = useQuery({
    queryKey: ['zusammenfuehren-vorschau', behaltenId, aufloesenId],
    enabled: !!aufloesenId,
    queryFn: () =>
      trpc.tasks.zusammenfuehrenVorschau.query({ behaltenId, aufloesenId: aufloesenId! }),
  })

  const zusammenfuehren = useMutation({
    mutationFn: () => trpc.tasks.zusammenfuehren.mutate({ behaltenId, aufloesenId: aufloesenId! }),
    onSuccess: async (ergebnis) => {
      /**
       * **Alles ungültig, was eine der beiden Zeilen zeigen könnte.** Eine der Aufgaben gibt es
       * nicht mehr; bliebe sie in einer Liste stehen, führte ein Klick darauf in ein leeres
       * Detail – der unangenehmste Zustand, den diese Funktion hinterlassen kann.
       */
      await nebenBestandVerwerfen(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['pools'] })
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
      await queryClient.invalidateQueries({ queryKey: ['vorhaben'] })
      fertig(ergebnis.behaltenId)
    },
    onError: (e: Error) => setFehler(e.message),
  })

  const partner = partnerId ? aufgaben.find((a) => a.id === partnerId) : null

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[6vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label="Aufgaben zusammenführen"
    >
      <div className="flex max-h-[min(90vh,100%)] w-full max-w-[680px] min-w-0 flex-col rounded-card border border-line bg-surface p-[18px] shadow-card sm:max-h-[min(84vh,100%)]">
        <h3 className="text-sm font-bold">Zusammenführen mit …</h3>
        <p className="mt-1 text-[11.5px] text-muted">
          Aus zwei Aufgaben wird eine. Kommentare, Verlauf, Unteraufgaben, Anhänge und die Bezüge
          zum Quellsystem ziehen mit; leere Felder werden aufgefüllt.{' '}
          <b className="text-ink2">Der Text der verschwindenden Aufgabe bleibt erhalten</b> – als
          Kommentar mit Titel, Datum und Herkunft.{' '}
          <b className="text-ink2">Rückgängig geht das nicht.</b>
        </p>

        {/* ── Schritt 1: welche? ─────────────────────────────────────── */}
        {!partner && (
          <>
            <input
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              autoFocus
              placeholder="Titel oder Ticketnummer …"
              aria-label="Aufgabe suchen"
              className="mt-3 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px]"
            />

            {!gesucht && (
              <p className="mt-2 text-[11px] text-muted">
                {vorschlaege.length > 0
                  ? 'Das klingt ähnlich – oder such selbst.'
                  : 'Nichts sieht von allein nach einer Dublette aus. Such über Titel oder Ticketnummer.'}
              </p>
            )}

            <div className="mt-1.5 min-h-0 flex-1 overflow-y-auto">
              {treffer.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setPartnerId(a.id)}
                  className="flex w-full cursor-pointer items-center gap-2 border-b border-line py-1.5 text-left text-[12.5px] last:border-b-0 hover:bg-[#fafaf8]"
                >
                  <span className="w-[80px] shrink-0 text-[11px] text-muted">
                    {TASK_STATUS_LABELS[a.status] ?? a.status}
                  </span>
                  <span className="min-w-0 flex-1 truncate" title={a.title}>
                    {a.title}
                  </span>
                  {a.externalKey && (
                    <SourceBadge source={a.sourceId as TaskSource} label={a.externalKey} />
                  )}
                </button>
              ))}
              {gesucht && treffer.length === 0 && (
                <p className="py-3 text-[12.5px] text-muted">
                  Nichts gefunden. Gesucht wird über Titel und Ticketnummern.
                </p>
              )}
            </div>
          </>
        )}

        {/* ── Schritt 2 und 3: Vorschau und Richtung ─────────────────── */}
        {partner && (
          <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
            <div className="rounded-[9px] border border-line2 p-2.5 text-[12.5px]">
              <Seite rolle="bleibt" titel={dieseBleibt ? titel : partner.title} />
              <div className="my-1.5 flex items-center gap-2">
                <span className="h-px flex-1 bg-line" />
                <button
                  type="button"
                  onClick={() => setDieseBleibt((v) => !v)}
                  className="cursor-pointer rounded-md border border-line2 px-2 py-[2px] text-[11px] text-ink2 hover:bg-page"
                >
                  ⇅ andersherum
                </button>
                <span className="h-px flex-1 bg-line" />
              </div>
              <Seite rolle="verschwindet" titel={dieseBleibt ? partner.title : titel} />
            </div>

            {vorschauLaedt && <p className="mt-2 text-[12px] text-muted">Wird gerechnet …</p>}

            {vorschau?.hindernis && (
              <p className="mt-2 rounded-md bg-[#fdf3f3] px-2 py-1.5 text-[12px] text-pool-sofort">
                {vorschau.hindernis}
              </p>
            )}

            {vorschau && !vorschau.hindernis && (
              <div className="mt-2.5 grid gap-2.5 sm:grid-cols-2">
                <Kasten titel="Zieht mit">
                  <Posten
                    zahl={vorschau.ziehtMit.kommentare}
                    wort="Kommentar"
                    mehrzahl="Kommentare"
                  />
                  <Posten
                    zahl={vorschau.ziehtMit.ereignisse}
                    wort="Ereignis"
                    mehrzahl="Ereignisse"
                  />
                  <Posten
                    zahl={vorschau.ziehtMit.unteraufgaben}
                    wort="Unteraufgabe"
                    mehrzahl="Unteraufgaben"
                  />
                  <Posten
                    zahl={vorschau.ziehtMit.quellbezuege}
                    wort="Quellbezug"
                    mehrzahl="Quellbezüge"
                  />
                  {/**
                   * **Der Text steht bei dem, was mitzieht** – nicht mehr im Verlustkasten.
                   * Beides ist wahr zu benennen: „wird ein Kommentar" ist eine andere Zusage
                   * als „füllt die leere Beschreibung", und wer gleich einen zweiten Absatz
                   * unter seiner Beschreibung findet, soll ihn hier angekündigt gelesen haben.
                   */}
                  {vorschau.textRettung === 'kommentar' && (
                    <p className="text-[11.5px]">Beschreibung als Kommentar</p>
                  )}
                  <p className="text-[11.5px]">Titel im Verlauf</p>
                  {vorschau.uebernommen.length > 0 && (
                    <p className="mt-1 text-[11.5px]">
                      Aufgefüllt: {vorschau.uebernommen.map((u) => u.bezeichnung).join(', ')}
                    </p>
                  )}
                </Kasten>

                {/**
                 * **Der Verlust steht gleichberechtigt daneben und nicht als Fußnote** – aber
                 * seit dem 28.08.2026 steht dort weder Titel noch Text ([E-276]). Übrig bleiben
                 * die Planungsangaben, an denen sich beide Aufgaben widersprechen; nur eine
                 * Fassung kann gelten, und das ist die der bleibenden.
                 */}
                <Kasten titel="Wird nicht übernommen" warnend>
                  {vorschau.verliert.length === 0 ? (
                    <p className="text-[11.5px] text-muted">
                      Nichts – die beiden widersprechen sich nirgends.
                    </p>
                  ) : (
                    <ul className="text-[11.5px]">
                      {vorschau.verliert.map((v) => (
                        <li key={v.feld}>{v.bezeichnung}</li>
                      ))}
                    </ul>
                  )}
                  <p className="mt-1 text-[11px] text-muted">
                    … jeweils die Fassung der verschwindenden Aufgabe. Welche das waren, hält der
                    Verlauf fest.
                  </p>
                </Kasten>
              </div>
            )}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
          {partner && (
            <Button
              variant="primary"
              disabled={!vorschau || !!vorschau.hindernis || zusammenfuehren.isPending}
              onClick={() => {
                setFehler(null)
                zusammenfuehren.mutate()
              }}
            >
              {zusammenfuehren.isPending ? 'Läuft …' : 'Zusammenführen'}
            </Button>
          )}
          {partner && <Button onClick={() => setPartnerId(null)}>Andere wählen</Button>}
          <Button onClick={schliessen}>Abbrechen</Button>
          {fehler && <span className="text-[12px] text-pool-sofort">{fehler}</span>}
        </div>
      </div>
    </div>
  )
}

/**
 * Eine der beiden Zeilen in der Gegenüberstellung.
 *
 * **Das Wort steht vor dem Titel, nicht dahinter.** „bleibt" und „verschwindet" sind die einzige
 * Angabe, die hier falsch verstanden werden kann – wer sie erst am Zeilenende liest, hat den
 * Titel schon der falschen Rolle zugeordnet.
 */
function Seite({ rolle, titel }: { rolle: 'bleibt' | 'verschwindet'; titel: string }) {
  const geht = rolle === 'verschwindet'
  return (
    <div className="flex items-baseline gap-2">
      <span
        className={`w-[92px] shrink-0 text-[10.5px] font-bold tracking-[0.04em] uppercase ${
          geht ? 'text-warn' : 'text-accent-deep'
        }`}
      >
        {rolle}
      </span>
      <span className={`min-w-0 flex-1 ${geht ? 'text-muted line-through' : 'font-semibold'}`}>
        {titel}
      </span>
    </div>
  )
}

function Kasten({
  titel,
  warnend,
  children,
}: {
  titel: string
  warnend?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      className={`rounded-[9px] border p-2.5 ${warnend ? 'border-warn/40 bg-[#fffdf6]' : 'border-line2'}`}
    >
      <h4 className="mb-1 text-[11px] font-bold tracking-[0.04em] uppercase">{titel}</h4>
      {children}
    </div>
  )
}

/** Eine Zählzeile – und **nichts**, wenn die Zahl null ist. Eine „0 Anhänge" ist keine Auskunft. */
function Posten({ zahl, wort, mehrzahl }: { zahl: number; wort: string; mehrzahl: string }) {
  if (zahl === 0) return null
  return (
    <p className="text-[11.5px]">
      {zahl} {zahl === 1 ? wort : mehrzahl}
    </p>
  )
}
